import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useSelection } from '../context/SelectionContext';
import { useTabletMode } from '../context/TabletModeContext';
import { trackAddToSelection } from '../lib/analytics';
import { toast } from 'sonner';
import { X, Check, Users, ChevronRight } from 'lucide-react';
import GuidedWizard from './GuidedWizard';

const ExperienceDetailPopup = ({ experience, onClose }) => {
  const { language, localize } = useLanguage();
  const { isDark } = useTheme();
  const { addItem } = useSelection();
  const { isTablet } = useTabletMode();
  const [showWizard, setShowWizard] = useState(false);

  if (!experience) return null;

  const loc = (field) => field?.[language] || field?.es || '';
  const hasOptions = (experience.options?.length > 0) || experience.requires_selection;
  const expName = localize(experience.name);
  const expCopy = experience.experience_copy ? loc(experience.experience_copy) : null;
  const expItems = experience.experience_items || [];
  const imgSrc = experience.images?.[0] || experience.image_url;
  const people = experience.experience_target_people;

  const handleAdd = () => {
    if (hasOptions) {
      setShowWizard(true);
      return;
    }
    trackAddToSelection(experience, 1, []);
    addItem(experience, 1, []);
    toast.success(`+ ${expName}`, { duration: 1200 });
    onClose();
  };

  const handleWizardComplete = () => {
    setShowWizard(false);
    onClose();
  };

  if (showWizard) {
    return (
      <GuidedWizard
        product={experience}
        index={0}
        onComplete={handleWizardComplete}
        onClose={() => setShowWizard(false)}
      />
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        data-testid="experience-detail-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 95,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          animation: 'fadeIn 0.2s ease',
        }}
      />

      {/* Panel — bottom sheet on mobile, centered modal on tablet */}
      <div
        data-testid="experience-detail-popup"
        style={isTablet ? {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '92%',
          maxWidth: '640px',
          zIndex: 96,
          background: isDark ? '#1e1e1e' : '#FBF5E8',
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
          animation: 'fadeIn 0.25s ease',
          maxHeight: '92vh',
          overflowY: 'auto',
        } : {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 96,
          background: isDark ? '#1e1e1e' : '#FBF5E8',
          borderRadius: '24px 24px 0 0',
          boxShadow: '0 -10px 40px rgba(0,0,0,0.3)',
          animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          maxHeight: '85vh',
          overflowY: 'auto',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          data-testid="experience-detail-close"
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            zIndex: 10,
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <X size={16} style={{ color: isDark ? '#aaa' : '#666' }} />
        </button>

        {/* Image */}
        {imgSrc && (
          <div style={{
            position: 'relative',
            height: isTablet ? '380px' : '200px',
            overflow: 'hidden',
            borderRadius: isTablet ? '24px 24px 0 0' : '24px 24px 0 0',
            background: isDark ? '#0E0E0D' : '#EFE6D2',
          }}>
            <img
              src={imgSrc}
              alt={expName}
              loading="eager"
              decoding="async"
              style={{
                width: '100%',
                height: '100%',
                objectFit: isTablet ? 'contain' : 'cover',
                objectPosition: 'center top',
                display: 'block',
              }}
            />
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 60%)',
            }} />
            {/* Price badge on image */}
            <div style={{
              position: 'absolute',
              bottom: '12px',
              right: '12px',
              padding: '6px 14px',
              borderRadius: '12px',
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(4px)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '18px',
            }}>
              {experience.price?.toFixed(2)}€
            </div>
            {/* People badge */}
            {people && people !== '1' && (
              <div style={{
                position: 'absolute',
                bottom: '12px',
                left: '12px',
                padding: '4px 10px',
                borderRadius: '20px',
                background: 'rgba(0,0,0,0.4)',
                backdropFilter: 'blur(4px)',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                <Users size={12} />
                {people} {language === 'es' ? 'personas' : language === 'en' ? 'people' : language === 'cat' ? 'persones' : 'personnes'}
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div style={{ padding: '20px 20px 32px' }}>
          {/* Name */}
          <h2
            data-testid="experience-detail-name"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '22px',
              fontWeight: 600,
              color: isDark ? '#f5f5f5' : '#2C2C2A',
              margin: '0 0 4px',
            }}
          >
            {expName}
          </h2>

          {/* Copy */}
          {expCopy && (
            <p style={{
              fontSize: '14px',
              color: isDark ? '#aaa' : '#5C5C59',
              margin: '0 0 16px',
              lineHeight: 1.4,
            }}>
              {expCopy}
            </p>
          )}

          {/* Items breakdown */}
          {expItems.length > 0 && (
            <div
              data-testid="experience-detail-items"
              style={{
                padding: '14px 16px',
                borderRadius: '16px',
                background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(183,140,60,0.06)',
                border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(183,140,60,0.12)',
                marginBottom: '20px',
              }}
            >
              <p style={{
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: isDark ? '#D4AF37' : '#B78C3C',
                margin: '0 0 10px',
              }}>
                {language === 'es' ? 'Incluye' : language === 'en' ? 'Includes' : language === 'cat' ? 'Inclou' : 'Comprend'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {expItems.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #B78C3C, #D4AF37)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Check size={11} color="#FBF5E8" />
                    </div>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: isDark ? '#ddd' : '#3C3C3A',
                      lineHeight: 1.3,
                    }}>
                      {loc(item)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add button */}
          <button
            onClick={handleAdd}
            data-testid="experience-detail-add-btn"
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: '16px',
              border: 'none',
              background: 'linear-gradient(135deg, #B78C3C 0%, #D4AF37 50%, #C9A84C 100%)',
              color: '#FBF5E8',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 4px 20px rgba(183,140,60,0.3)',
              transition: 'transform 0.15s',
            }}
          >
            {hasOptions
              ? (language === 'es' ? 'Personalizar y añadir' : language === 'en' ? 'Customize and add' : language === 'cat' ? 'Personalitzar i afegir' : 'Personnaliser et ajouter')
              : (language === 'es' ? 'Añadir al pedido' : language === 'en' ? 'Add to order' : language === 'cat' ? 'Afegir a la comanda' : 'Ajouter à la commande')
            }
            {' '} — {experience.price?.toFixed(2)}€
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </>
  );
};

export default ExperienceDetailPopup;
